import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Icon,
  Stack,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { RecipeDetailResponse } from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import {
  buyerCurrencyFormatter,
  getBannerReferer,
  getItemCardTrackingParams,
  trackEvent,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import {
  handleCogSummary,
  useRouteQuery,
  useRouteRedirection,
} from '@diamond/sol-buyer/utils';
import {
  useIsAuthenticated,
  useShipToStore,
  useUpdateBatchCart,
} from '@diamond/sol-buyer-context';
import { HelpOutline } from '@mui/icons-material';
import { isAxiosError } from 'axios';
import { useRef } from 'react';

import { RecipeCartProductSelectMobile } from './RecipeCartProductSelectMobile';
import { useRecipeAddToCart } from './useRecipeAddToCart';

type RecipeCartModalProps = {
  data: RecipeDetailResponse;
  countItems: number;
  isDisabled?: boolean;
  isRecommended: boolean;
};

export function RecipeCartBottomSheet({
  data,
  countItems,
  isDisabled,
  isRecommended,
}: RecipeCartModalProps) {
  const btnRef = useRef<HTMLButtonElement>(null);

  const slug = useRouteQuery()['recipesId'];
  const isAuthenticated = useIsAuthenticated();
  const { withAuth } = useRouteRedirection({ isAuthenticated });
  const warehouse = useShipToStore((s) => s.selectedShipToId);

  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    products,
    totalUnavailableProduct,
    selectedAvailableProducts,
    totalHPP,
    totalProduct,
    handleChangeProduct,
    handleDeleteProduct,
    resetState,
  } = useRecipeAddToCart(data, isRecommended);

  const batchUpdateCartMutation = useUpdateBatchCart({
    onSuccess() {
      showToast(
        toast,
        'success',
        `${totalProduct} Produk berhasil masuk keranjang`
      );

      const bannerReferer = getBannerReferer();

      trackEvent(TRACKING_EVENTS.ADD_TO_CART, {
        currency: 'IDR',
        value: totalHPP,
        items: selectedAvailableProducts.map((product, index) => {
          const trackingParams = getItemCardTrackingParams(
            product as unknown as Parameters<
              typeof getItemCardTrackingParams
            >[0], // Cast to avoid type mismatch
            1,
            TRACKING_PAGES.RECIPE_DETAIL,
            TRACKING_PAGES.RECIPE_DETAIL
          );

          return {
            ...trackingParams,
            index: index + 1,
            affiliation: 'Sukanda Onelink',
            location_id: warehouse,
          };
        }),
        section_name: TRACKING_PAGES.RECIPE_DETAIL,
        page_name: TRACKING_PAGES.RECIPE_DETAIL,
        id_recipe: data?.id, // Keep recipe-specific fields for backward compatibility
        recipe_name: data?.title,
        recipe_slug: slug,
        total_products: totalProduct,
        warehouse_id: warehouse,
        ...(bannerReferer && {
          referer_source: 'banner',
          banner_id: bannerReferer.banner_id,
          banner_name: bannerReferer.banner_name,
        }),
      });
      onClose();
    },
    onError(err) {
      let message = 'Unknown Error';
      if (isAxiosError(err)) {
        message = err.response?.data?.message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      showToast(toast, 'error', message);
    },
  });

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <>
      <Button
        ref={btnRef}
        onClick={() => {
          withAuth(onOpen, 'recipes', slug);
        }}
        color="white"
        _hover={{ bg: 'blue.700' }}
        w="full"
        size="md"
        isDisabled={isDisabled}
      >
        + Keranjang ({countItems})
      </Button>
      <Drawer
        isOpen={isOpen}
        placement="bottom"
        onClose={handleClose}
        finalFocusRef={btnRef}
      >
        <DrawerOverlay />
        <DrawerContent borderTopRadius="xl">
          <DrawerHeader textAlign="center">
            Konfirmasi Produk dari Resep
            <Text
              textAlign="center"
              fontWeight="normal"
              fontSize="sm"
              maxW="sm"
              mx="auto"
              mt="2"
            >
              Tekan Panah untuk ubah produk dari resep. <br /> Ganti kuantitas
              dapat dilakukan pada halaman keranjang.
            </Text>
          </DrawerHeader>

          <DrawerCloseButton />
          <DrawerBody px="0">
            <VStack alignItems="flex-start" spacing="4" w="full">
              {totalUnavailableProduct > 0 ? (
                <Flex
                  w="full"
                  px="8"
                  py="2"
                  bg="gray.customGray"
                  color="red"
                  alignItems="center"
                  fontSize="sm"
                >
                  {totalUnavailableProduct} bahan pada resep saat ini tidak
                  tersedia
                </Flex>
              ) : null}

              {products.map(
                ({ order: product, selected: selectedIdx }, index) => (
                  <RecipeCartProductSelectMobile
                    key={product[0].id}
                    product={product}
                    selectedProductIdx={selectedIdx}
                    handleNavigate={(productId) =>
                      handleChangeProduct({
                        index,
                        productId,
                      })
                    }
                    handleDelete={() => handleDeleteProduct(index)}
                    px="4"
                  />
                )
              )}
              <Flex w="full" px="8" py="2.5" bg="#D9F3FF" alignItems="center">
                <Text flex="1" fontSize="sm" fontWeight="medium">
                  Total harga • {totalProduct} Produk
                </Text>
                <Text fontWeight="bold" fontSize="xl">
                  {buyerCurrencyFormatter(totalHPP)}
                </Text>
              </Flex>
            </VStack>
          </DrawerBody>
          <DrawerFooter
            as={Flex}
            borderTopRadius="3xl"
            bg="white"
            px={6}
            py={3}
            w="full"
            minH="100px"
            bottom={0}
            insetInline={0}
            zIndex={999}
            alignItems="center"
            justifyContent="stretch"
          >
            <Stack spacing="0" flex="0 0 60%">
              <Text fontSize="xs" color="#006BD1">
                Estimasi HPP per Porsi{' '}
                <Icon mt="-1" fontSize="18px" as={HelpOutline} />
              </Text>
              <Text fontSize="xl" fontWeight="bold">
                {buyerCurrencyFormatter(
                  handleCogSummary(selectedAvailableProducts, true)
                )}
              </Text>
            </Stack>
            <Button
              isDisabled={totalProduct === 0 || totalHPP === 0}
              colorScheme="blue"
              w="full"
              size="md"
              onClick={() =>
                batchUpdateCartMutation.mutate(
                  selectedAvailableProducts.map((p) => ({
                    product_id: p.id,
                    quantity: p.minimum_order_quantity,
                    warehouse_id: warehouse,
                    note: '',
                  }))
                )
              }
            >
              Konfirmasi
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
